import { isUnlimitedTierCode, normalizeTierCode, UNLIMITED_TIER_VALUE } from '../components/ProductAddons/licenseRules';
import { describeInvalidEndingVersion, isEndingVersion } from '../components/ProductAddons/versionRules';

export const EMPTY_FIELDS_MESSAGE = 'Cannot have empty fields. Please check all the fields from all the tabs.';

class AddonValidationService {
	static validate = (values) => {
		const errors = {};
		const fields = [
			'name',
			'translations.it.name',
			'translations.es.name',
			'summary',
			'translations.it.summary',
			'translations.es.summary',
			'description',
			'translations.it.description',
			'translations.es.description',
			'summary',
			'description',
			'maxPeriodYears',
			'code',
			'minProductVersion',
		];

		fields.forEach((field) => {
			const fieldValue = AddonValidationService.getNestedValue(values, field);
			if (AddonValidationService.validators.required(fieldValue)) {
				AddonValidationService.setNestedError(errors, field, 'Cannot be empty');
			}
		});

		if (!isEndingVersion(values.maxProductVersion)) {
			errors.maxProductVersion = describeInvalidEndingVersion(values.maxProductVersion);
		}

		if (values.addonTiers && values.addonTiers.length > 0) {
			const tiersArrayErrors = AddonValidationService.validateTiers(values.addonTiers);
			if (tiersArrayErrors.length > 0) {
				errors.addonTiers = tiersArrayErrors;
			}
		}

		return errors;
	};

	/*
	 * A tier needs a name, a code no other tier of the license uses and a value: 0 or more in the base unit, a whole
	 * number since the server stores an integer, or exactly -1 on the tier coded `unlimited`. The errors sit at the
	 * index of their tier, so the array is sparse.
	 */
	static validateTiers = (tiers) => {
		const tiersArrayErrors = [];
		const codeCounts = tiers.reduce((counts, tier) => {
			const code = normalizeTierCode(tier.code);
			if (code) {
				counts[code] = (counts[code] || 0) + 1;
			}

			return counts;
		}, {});

		tiers.forEach((tier, index) => {
			const tierErrors = {};
			if (AddonValidationService.validators.required(tier.name)) {
				tierErrors.name = 'Name cannot be empty';
			}
			if (AddonValidationService.validators.required(tier.code)) {
				tierErrors.code = 'Code cannot be empty';
			} else if (codeCounts[normalizeTierCode(tier.code)] > 1) {
				tierErrors.code = 'Code is used by another tier';
			}
			const valueError = AddonValidationService.validateTierValue(tier);
			if (valueError) {
				tierErrors.value = valueError;
			}
			if (Object.keys(tierErrors).length > 0) {
				tiersArrayErrors[index] = tierErrors;
			}
		});

		return tiersArrayErrors;
	};

	static validateTierValue = (tier) => {
		const { value } = tier;

		if (isUnlimitedTierCode(tier.code)) {
			return value === UNLIMITED_TIER_VALUE ? null : 'An Unlimited tier has the value -1';
		}
		if (value === null || value === undefined || value === '') {
			return 'Value cannot be empty';
		}
		if (!AddonValidationService.validators.finiteNumber(value)) {
			return 'Value must be a number';
		}
		if (Number(value) < 0) {
			return 'Value cannot be negative';
		}
		if (!Number.isInteger(Number(value))) {
			return 'Value must be a whole number in the smallest unit';
		}

		return null;
	};

	// What the toast says when the save is refused in the form: the ending version's own message, else the generic one.
	static submitErrorMessage = (errors) => {
		return errors?.maxProductVersion || EMPTY_FIELDS_MESSAGE;
	};

	static getNestedValue(obj, path) {
		return path.split('.').reduce((acc, key) => { return (acc && acc[key] !== undefined ? acc[key] : undefined); }, obj);
	}

	static setNestedError(obj, path, message) {
		const keys = path.split('.');
		keys.reduce((acc, key, index) => {
			if (index === keys.length - 1) {
				acc[key] = message;
			} else {
				acc[key] = acc[key] || {};
			}

			return acc[key];
		}, obj);
	}

	static validators = {
		required: (value) => { return !value || value === undefined || value.length === 0; },
		finiteNumber: (value) => { return typeof value !== 'boolean' && String(value).trim() !== '' && Number.isFinite(Number(value)); },
	};
}

export default AddonValidationService;
